import { Globe, Lock, Users } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

export function ChatPrivacyFields({
  visibility,
  onVisibility,
  memberIds,
  onMembers,
  members,
}: {
  visibility: "PRIVATE" | "SHARED" | "PROJECT";
  onVisibility(value: "PRIVATE" | "SHARED" | "PROJECT"): void;
  memberIds: string[];
  onMembers(ids: string[]): void;
  members: { userId: string; name: string }[];
}) {
  const fr = useI18n().language === "fr";
  return (
    <div className="space-y-3">
      <div
        className="grid grid-cols-3 gap-2"
        role="radiogroup"
        aria-label={
          fr ? "Visibilité de la conversation" : "Conversation visibility"
        }
      >
        {[
          {
            value: "PRIVATE" as const,
            title: fr ? "Privée" : "Private",
            description: fr ? "Vous uniquement" : "Only you",
            icon: Lock,
          },
          {
            value: "SHARED" as const,
            title: fr ? "Partagée" : "Shared",
            description: fr ? "Membres choisis" : "Selected people",
            icon: Users,
          },
          {
            value: "PROJECT" as const,
            title: fr ? "Projet" : "Project",
            description: fr ? "Tout le projet" : "Whole project",
            icon: Globe,
          },
        ].map((item) => (
          <button
            key={item.value}
            type="button"
            role="radio"
            aria-checked={visibility === item.value}
            onClick={() => onVisibility(item.value)}
            className={cn(
              "flex flex-col items-center gap-1.5 rounded-xl border p-3 text-center transition-colors",
              visibility === item.value
                ? "border-primary/60 bg-primary/10 text-primary"
                : "border-white/10 bg-white/[0.02] hover:bg-white/5",
            )}
          >
            <item.icon className="h-5 w-5" />
            <span className="text-sm font-medium">{item.title}</span>
            <span className="text-[11px] text-foreground/60">
              {item.description}
            </span>
          </button>
        ))}
      </div>
      {visibility === "SHARED" && (
        <div className="max-h-56 overflow-y-auto rounded-lg border border-white/10 p-2 space-y-1">
          {members.map((member) => (
            <label
              key={member.userId}
              className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-white/5 text-sm"
            >
              <Checkbox
                checked={memberIds.includes(member.userId)}
                onCheckedChange={(checked) =>
                  onMembers(
                    checked
                      ? [...new Set([...memberIds, member.userId])]
                      : memberIds.filter((id) => id !== member.userId),
                  )
                }
              />
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs text-primary">
                {member.name.slice(0, 2).toLocaleUpperCase()}
              </span>
              <span>{member.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
